import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InstagramIcon from '@mui/icons-material/Instagram';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
// Category icons
import TvIcon from '@mui/icons-material/Tv';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill';
import KitchenIcon from '@mui/icons-material/Kitchen';
import HotelIcon from '@mui/icons-material/Hotel';
import ChairIcon from '@mui/icons-material/Chair';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import ShowerIcon from '@mui/icons-material/Shower';
import BlindsTwoToneIcon from '@mui/icons-material/BlindsTwoTone';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import DoorFrontIcon from '@mui/icons-material/DoorFront';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import CeilingIcon from '@mui/icons-material/SensorDoor';

import {
  MapContainer, TileLayer, Marker, Polyline,
  Tooltip as LeafletTooltip, useMapEvents, useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import Navbar from '../components/Header10';
import Footer from '../components/Footer5';
import {
  GREEN, GREEN_HOVER, CHARCOAL, BORDER, MUTED, MUTED_FAINT, STONE50,
  labelMono, bodyFont,
} from '../../theme/tokens2';
import { apiFetch } from '../../api/apiClient';
import { InlineAlert } from '../../shared/components/status';

// ─── Constants ────────────────────────────────────────────────────────────────

// Paste the key CARTO emails you (from carto.com/basemaps/apikey) below.
const CARTO_API_KEY    = 'cb1_2yo0_1_d0b0fee99a43cce364ffad24';
const CARTO_URL        = `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_API_KEY}`;
const DEFAULT_CENTER   = [1.3521, 103.8198];
const TOOLTIP_MIN_ZOOM = 11;
const LS_KEY           = 'dwell_bookmarks'; // localStorage key

// ─── Category definitions ─────────────────────────────────────────────────────
const CATEGORY_DEFS = [
  { key: 'televisio', label: 'Television, Projector',             Icon: TvIcon },
  { key: 'washerdry', label: 'Washer, Dryer, Laundry Racks',      Icon: LocalLaundryServiceIcon },
  { key: 'aircondit', label: 'Air Conditioner',                   Icon: AcUnitIcon },
  { key: 'waterfilt', label: 'Water Filter / Dispenser',          Icon: WaterDropIcon },
  { key: 'hobhoodov', label: 'Hob, Hood, Oven, Microwave',        Icon: OutdoorGrillIcon },
  { key: 'fridge',    label: 'Fridge',                            Icon: KitchenIcon },
  { key: 'bed',       label: 'Bed',                               Icon: HotelIcon },
  { key: 'sofadinin', label: 'Sofa, Dining Table & Chairs',       Icon: ChairIcon },
  { key: 'lightingl', label: 'Lighting, Lamps',                   Icon: LightbulbIcon },
  { key: 'kitchensi', label: 'Kitchen Sink, Tap',                 Icon: PlumbingIcon },
  { key: 'toiletsin', label: 'Toilet Sink, Tap, Shower, Heater',  Icon: ShowerIcon },
  { key: 'curtainsb', label: 'Curtains, Blinds, Shades',          Icon: BlindsTwoToneIcon },
  { key: 'tilesviny', label: 'Tiles, Vinyl, Flooring',            Icon: GridViewIcon },
  { key: 'laminates', label: 'Laminates',                         Icon: TableRowsIcon },
  { key: 'rugscarpe', label: 'Rugs, Carpets',                     Icon: SquareFootIcon },
  { key: 'digitaldo', label: 'Digital Door Lock, Doors',          Icon: DoorFrontIcon },
  { key: 'wallpaper', label: 'Wallpaper',                         Icon: WallpaperIcon },
  { key: 'ceilingfa', label: 'Ceiling Fans',                      Icon: CeilingIcon },
];

// ─── Marker icons ─────────────────────────────────────────────────────────────
const makeTeardropIcon = (color) => L.divIcon({
  html: `<div style="
    width:24px;height:24px;background:${color};
    border:2.5px solid white;border-radius:50% 50% 50% 0;
    transform:rotate(-45deg);box-shadow:0 2px 8px rgba(0,0,0,0.25);
  "></div>`,
  className: '', iconSize: [24, 24], iconAnchor: [12, 24], popupAnchor: [0, -28],
});
const greenIcon  = makeTeardropIcon(GREEN);
const yellowIcon = makeTeardropIcon('#f59e0b');
const redIcon    = makeTeardropIcon('#ef4444');

// ─── Shared slider sx ────────────────────────────────────────────────────────
const sliderSx = {
  color: GREEN,
  '& .MuiSlider-track': { color: GREEN },
  '& .MuiSlider-rail':  { color: '#c8e6c9' },
  '& .MuiSlider-thumb': { width: 12, height: 12, color: GREEN },
  '& .MuiSlider-thumb.Mui-focusVisible, & .MuiSlider-thumb:hover': {
    boxShadow: '0 0 0 8px rgba(33,131,51,0.12)',
  },
  '& .MuiSlider-valueLabel': {
    backgroundColor: CHARCOAL, ...labelMono, fontSize: '9px', borderRadius: 0,
  },
};

// ─── Nearest-neighbour sort ───────────────────────────────────────────────────
// Sorts vendors into a logical visit order starting from the westernmost pin.
// Simple O(n²) nearest-neighbour — more than sufficient for ≤~10 bookmarks.
function sortByNearestNeighbour(vendors) {
  if (vendors.length <= 2) return [...vendors];
  const remaining = [...vendors];
  // Start from westernmost pin (lowest longitude)
  const startIdx = remaining.reduce(
    (bestIdx, v, i) => v.longitude < remaining[bestIdx].longitude ? i : bestIdx, 0
  );
  const sorted = remaining.splice(startIdx, 1);
  while (remaining.length > 0) {
    const last = sorted[sorted.length - 1];
    let closestIdx = 0;
    let closestDist = Infinity;
    remaining.forEach((v, i) => {
      const dist = Math.hypot(v.latitude - last.latitude, v.longitude - last.longitude);
      if (dist < closestDist) { closestDist = dist; closestIdx = i; }
    });
    sorted.push(remaining.splice(closestIdx, 1)[0]);
  }
  return sorted;
}

// ─── Build Google Maps multi-stop URL ────────────────────────────────────────
// Constructs a directions URL with origin, destination, and waypoints.
function buildGoogleMapsUrl(orderedVendors) {
  if (orderedVendors.length === 0) return null;
  if (orderedVendors.length === 1) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(orderedVendors[0].address || orderedVendors[0].name)}`;
  }
  const origin      = encodeURIComponent(orderedVendors[0].address || orderedVendors[0].name);
  const destination = encodeURIComponent(orderedVendors[orderedVendors.length - 1].address || orderedVendors[orderedVendors.length - 1].name);
  const waypoints   = orderedVendors
    .slice(1, -1)
    .map(v => encodeURIComponent(v.address || v.name))
    .join('|');
  return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${waypoints}` : ''}`;
}

// ─── ZoomWatcher ─────────────────────────────────────────────────────────────
function ZoomWatcher({ onZoomChange }) {
  useMapEvents({ zoomend: (e) => onZoomChange(e.target.getZoom()) });
  return null;
}

// ─── FitBoundsToBookmarks ────────────────────────────────────────────────────
// Fires once when trip planning mode is entered.
// Pans and zooms the map to fit all bookmarked pins within the VISIBLE area.
//
// paddingTop/Right/Bottom/Left mirror Leaflet's paddingTopLeft / paddingBottomRight:
//   Desktop: modest uniform padding — no major obstructions on the map itself
//            (the left panel is outside the map container, not over it).
//   Mobile:  large bottom padding to push pins above the 55vh bottom sheet,
//            and top padding to clear the 64px navbar.
function FitBoundsToBookmarks({ vendors, active, paddingTop = 48, paddingRight = 48, paddingBottom = 48, paddingLeft = 48 }) {
  const map = useMap();
  useEffect(() => {
    if (!active) return;
    const withCoords = vendors.filter(
      v => v.latitude != null && v.longitude != null && isFinite(v.latitude) && isFinite(v.longitude)
    );
    if (withCoords.length === 0) return;
    const bounds = L.latLngBounds(withCoords.map(v => [v.latitude, v.longitude]));
    map.fitBounds(bounds, {
      paddingTopLeft:     [paddingLeft, paddingTop],
      paddingBottomRight: [paddingRight, paddingBottom],
      maxZoom: 15,
    });
  }, [active]); // intentionally only re-fires when active changes, not on every vendor update
  return null;
}

// ─── FitBoundsToCategory ──────────────────────────────────────────────────────
// Fires whenever the active category or filtered vendor list changes.
// Fits the map to show all vendors in the current filtered set.
// Does nothing if there are no vendors with coordinates (avoids error on empty category).
// Same asymmetric padding props as FitBoundsToBookmarks.
function FitBoundsToCategory({ filteredVendors, paddingTop = 48, paddingRight = 48, paddingBottom = 48, paddingLeft = 48 }) {
  const map = useMap();

  useEffect(() => {
    const withCoords = filteredVendors.filter(
      v => v.latitude != null && v.longitude != null && isFinite(v.latitude) && isFinite(v.longitude)
    );
    // Guard: do nothing if no mappable vendors (empty category or still loading)
    if (withCoords.length === 0) return;

    const bounds = L.latLngBounds(withCoords.map(v => [v.latitude, v.longitude]));
    map.fitBounds(bounds, {
      paddingTopLeft:     [paddingLeft, paddingTop],
      paddingBottomRight: [paddingRight, paddingBottom],
      maxZoom: 15,
      animate: true,
    });
  }, [filteredVendors]); // fires only when fetch resolves with new data — not on category click itself

  return null;
}


// Pans the map in a single smooth motion so the selected pin lands in the
// centre of the *visible* (unobscured) portion of the map container.
//
// Props:
//   vendor      — the selected vendor object (needs latitude, longitude)
//   panelWidth  — px width of the panel obscuring the RIGHT of the map (desktop)
//   panelHeight — px height of the sheet obscuring the BOTTOM of the map (mobile)
//
// How the single-step calculation works:
//   1. Convert the pin's lat/lng to its current pixel position in the container.
//   2. Calculate where we WANT the pin to appear in pixel space:
//        x target = centre of the visible width  (container width  - panelWidth)  / 2
//        y target = centre of the visible height (container height - panelHeight) / 2
//   3. The map centre must shift by the same pixel delta as the pin needs to move.
//      Find the new map centre by offsetting the current centre pixel by that delta,
//      then converting back to lat/lng.
//   4. panTo that single destination — one call, one animation, no lurching.
function PanToPin({ vendor, panelWidth = 0, panelHeight = 0 }) {
  const map = useMap();

  useEffect(() => {
    if (!vendor || vendor.latitude == null || vendor.longitude == null) return;

    const containerSize = map.getSize(); // { x: containerWidth, y: containerHeight }

    // Step 1 — where is the pin right now in pixel space?
    const pinPixel = map.latLngToContainerPoint([vendor.latitude, vendor.longitude]);

    // Step 2 — where do we WANT the pin to be?
    // Centre of the visible area (container minus the obscuring panel).
    const targetPixel = {
      x: (containerSize.x - panelWidth)  / 2,
      y: (containerSize.y - panelHeight) / 2,
    };

    // Step 3 — how much does the pin need to move in pixel space?
    // The map centre must shift by the same delta in the opposite direction.
    const currentCentrePixel = {
      x: containerSize.x / 2,
      y: containerSize.y / 2,
    };
    const newCentrePixel = {
      x: currentCentrePixel.x + (pinPixel.x - targetPixel.x),
      y: currentCentrePixel.y + (pinPixel.y - targetPixel.y),
    };

    // Step 4 — convert that pixel position back to lat/lng and pan there directly.
    const newCentreLatLng = map.containerPointToLatLng(newCentrePixel);
    map.panTo(newCentreLatLng, { animate: true, duration: 0.5 });

  }, [vendor?.googlePlaceId]); // re-fires only when the selected vendor changes

  return null;
}

// ─── TripPolyline ─────────────────────────────────────────────────────────────
// Draws a straight line connecting bookmarked pins in visit order.
// Only rendered in trip planning mode.
function TripPolyline({ orderedVendors }) {
  const positions = orderedVendors
    .filter(v => v.latitude != null && v.longitude != null)
    .map(v => [v.latitude, v.longitude]);
  if (positions.length < 2) return null;
  return (
    <Polyline
      positions={positions}
      pathOptions={{ color: '#ef4444', weight: 2, dashArray: '6 6', opacity: 0.7 }}
    />
  );
}

// ─── MapMarkers ───────────────────────────────────────────────────────────────
// In normal mode: renders filteredVendors + any bookmarked vendors not in the list.
// In trip planning mode: renders only bookmarked vendors.
function MapMarkers({
  filteredVendors, bookmarkedVendors, tripPlanningMode,
  getMarkerIcon, markerRefs, onMarkerClick, showTooltips,
}) {
  // Build the set of vendors to display
  const vendorsToShow = useMemo(() => {
    if (tripPlanningMode) {
      // Only show bookmarks in trip planning mode
      return bookmarkedVendors.filter(
        v => v.latitude != null && v.longitude != null && isFinite(v.latitude) && isFinite(v.longitude)
      );
    }
    // Normal mode: show filtered vendors + bookmarks that aren't already in the list
    const filteredIds = new Set(filteredVendors.map(v => v.googlePlaceId));
    const bookmarkOnly = bookmarkedVendors.filter(v => !filteredIds.has(v.googlePlaceId));
    return [
      ...filteredVendors.filter(
        v => v.latitude != null && v.longitude != null && isFinite(v.latitude) && isFinite(v.longitude)
      ),
      ...bookmarkOnly.filter(
        v => v.latitude != null && v.longitude != null && isFinite(v.latitude) && isFinite(v.longitude)
      ),
    ];
  }, [filteredVendors, bookmarkedVendors, tripPlanningMode]);

  return vendorsToShow.map(v => (
    <Marker
      key={v.googlePlaceId}
      ref={ref => { if (ref) markerRefs.current[v.googlePlaceId] = ref; }}
      position={[v.latitude, v.longitude]}
      icon={getMarkerIcon(v)}
      eventHandlers={{ click: () => onMarkerClick(v) }}
    >
      {showTooltips && (
        <LeafletTooltip direction="top" offset={[0, -28]} permanent
          style={{
            backgroundColor: 'rgba(18,18,18,0.88)', color: '#fff',
            border: 'none', borderRadius: 0, padding: '4px 10px',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1.3 }}>
            {v.name}
          </div>
          {v.googleRating != null && (
            <div style={{ fontSize: 10, color: '#fbbf24', whiteSpace: 'nowrap' }}>
              ★ {v.googleRating.toFixed(1)} ({v.googleRatingTotal?.toLocaleString()})
            </div>
          )}
        </LeafletTooltip>
      )}
    </Marker>
  ));
}

// ─── RatingFilterBlock ────────────────────────────────────────────────────────
// shownCount is optional — when provided, renders the vendor count on the same
// line as the heading, right-aligned (used on desktop to save vertical space).
function RatingFilterBlock({ ratingRange, setRatingRange, ratingTotalRange, setRatingTotalRange, shownCount }) {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ ...labelMono, color: GREEN, fontSize: '9px' }}>
          FILTER BY RATING
        </Typography>
        {shownCount != null && (
          <Typography sx={{ ...labelMono, color: MUTED_FAINT, fontSize: '8px' }}>
            {shownCount} Showrooms
          </Typography>
        )}
      </Box>
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ ...labelMono, color: MUTED, fontSize: '9px' }}>GOOGLE RATING</Typography>
          <Typography sx={{ ...labelMono, color: CHARCOAL, fontSize: '9px' }}>
            {ratingRange[0].toFixed(1)} – {ratingRange[1].toFixed(1)}
          </Typography>
        </Box>
        <Slider value={ratingRange} min={0} max={5} step={0.1}
          onChange={(_, v) => setRatingRange(v)} valueLabelDisplay="auto"
          sx={{ ...sliderSx, width: '95%' }} />
      </Box>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ ...labelMono, color: MUTED, fontSize: '9px' }}>NO. OF RATINGS</Typography>
          <Typography sx={{ ...labelMono, color: CHARCOAL, fontSize: '9px' }}>
            {ratingTotalRange[0].toLocaleString()} – {ratingTotalRange[1] === 5000 ? '5K+' : ratingTotalRange[1].toLocaleString()}
          </Typography>
        </Box>
        <Slider value={ratingTotalRange} min={0} max={5000} step={500}
          onChange={(_, v) => setRatingTotalRange(v)} valueLabelDisplay="auto"
          valueLabelFormat={v => v === 5000 ? '5K+' : `${(v / 1000).toFixed(1)}K`}
          sx={{ ...sliderSx, width: '95%' }} />
      </Box>
    </>
  );
}

// ─── CategoryItem ─────────────────────────────────────────────────────────────
function CategoryItem({ cat, isActive, isExpanded, vendors, selectedVendorId, onSelect, onToggle }) {
  const [hovered, setHovered] = useState(false);
  const { Icon } = cat;

  const featured = vendors
    .filter(v => { const val = v[cat.key]; return typeof val === 'number' && val >= 1 && val <= 5; })
    .sort((a, b) => (a[cat.key] ?? 999) - (b[cat.key] ?? 999));

  return (
    <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
      <Box component="button" onClick={onToggle}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        sx={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5,
          backgroundColor: isActive ? 'rgba(33,131,51,0.05)' : hovered ? STONE50 : '#fff',
          borderLeft: `2px solid ${isActive ? GREEN : 'transparent'}`,
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Icon sx={{
            fontSize: '0.95rem', flexShrink: 0,
            color: isActive ? GREEN : 'rgba(0,0,0,0.30)',
            transition: 'color 0.15s ease',
          }} />
          <Typography sx={{
            fontSize: '0.78rem', fontWeight: isActive ? 500 : 400,
            color: isActive ? GREEN : CHARCOAL, ...bodyFont, textAlign: 'left',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            transition: 'color 0.15s ease',
          }}>
            {cat.label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, ml: 1 }}>
          {featured.length > 0 && (
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT }}>{featured.length}</Typography>
          )}
          <KeyboardArrowDownIcon sx={{
            fontSize: '0.85rem', color: MUTED,
            transition: 'transform 0.2s ease',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }} />
        </Box>
      </Box>

      {/* Featured vendor cards — expanded accordion */}
      <Box sx={{
        overflow: 'hidden',
        maxHeight: isExpanded ? `${Math.max(featured.length, 1) * 88 + 64}px` : '0px',
        transition: 'max-height 0.3s cubic-bezier(0.22,1,0.36,1)',
        backgroundColor: STONE50,
        px: 1.5, pb: isExpanded ? 1.5 : 0,
      }}>
        <Typography sx={{ ...labelMono, fontSize: '8px', color: GREEN, pt: 1.5, pb: 1, px: 1 }}>
          FEATURED
        </Typography>
        {featured.length === 0 ? (
          <Box sx={{ px: 1, pb: 1.5 }}>
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT }}>No featured vendors yet.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {featured.map(v => {
              const isSelected = selectedVendorId === v.googlePlaceId;
              return (
                <Box key={v.googlePlaceId} component="button" onClick={() => onSelect(v.googlePlaceId)}
                  sx={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', p: 0 }}>
                  <Box sx={{
                    px: 1.5, py: 1.25,
                    backgroundColor: isSelected ? 'rgba(33,131,51,0.08)' : '#fff',
                    border: `1px solid ${isSelected ? 'rgba(33,131,51,0.35)' : BORDER}`,
                    borderLeft: `3px solid ${isSelected ? GREEN : 'rgba(33,131,51,0.25)'}`,
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    '&:hover': { backgroundColor: 'rgba(33,131,51,0.05)', borderColor: 'rgba(33,131,51,0.35)' },
                  }}>
                    <Typography sx={{
                      fontSize: '0.78rem', fontWeight: 600, color: CHARCOAL,
                      ...bodyFont, lineHeight: 1.3,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {v.name}
                    </Typography>
                    {v.googleRating != null && (
                      <Typography sx={{ ...labelMono, fontSize: '8px', color: '#f59e0b', mt: 0.5 }}>
                        ★ {v.googleRating.toFixed(1)}{' '}
                        <Typography component="span" sx={{ color: MUTED_FAINT, fontSize: '8px' }}>
                          ({v.googleRatingTotal?.toLocaleString()} reviews)
                        </Typography>
                      </Typography>
                    )}
                    {v.address && (
                      <Typography sx={{
                        fontSize: '0.7rem', color: MUTED_FAINT, mt: 0.25,
                        ...bodyFont, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {v.address}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ─── VisitPlanPanel ───────────────────────────────────────────────────────────
// Shown in both desktop left panel and mobile Plan My Route tab when
// trip planning mode is active. Lists bookmarks in visit order with
// drag handles to reorder, and the Google Maps CTA at the bottom.
//
// Reordering uses BOTH HTML5 drag events (mouse/desktop) AND touch events
// (mobile) because the HTML5 drag API is mouse-only and does nothing on
// touch screens.
//
// Touch implementation:
//   onTouchStart  — record which item index the finger pressed
//   onTouchMove   — find which item the finger is currently over by hit-testing
//                   elementFromPoint, swap if different from last position
//   onTouchEnd    — clean up
function VisitPlanPanel({ visitOrder, onReorder, onExitTripPlanning, googleMapsUrl }) {
  const dragIndex    = useRef(null); // for mouse drag
  const touchIndex   = useRef(null); // for touch drag
  const itemRefs     = useRef([]);   // refs to each list item DOM node

  // ── Mouse drag handlers (desktop) ─────────────────────────────────────────
  const handleDragStart = (i) => { dragIndex.current = i; };

  const handleDragOver = (e, i) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === i) return;
    const updated = [...visitOrder];
    const [moved] = updated.splice(dragIndex.current, 1);
    updated.splice(i, 0, moved);
    dragIndex.current = i;
    onReorder(updated);
  };

  const handleDragEnd = () => { dragIndex.current = null; };

  // ── Touch drag handlers (mobile) ──────────────────────────────────────────
  const handleTouchStart = (e, i) => {
    touchIndex.current = i;
    // Prevent the page/sheet from scrolling while the user is dragging an item
    e.stopPropagation();
  };

  const handleTouchMove = (e) => {
    e.preventDefault(); // prevent scroll during drag
    e.stopPropagation();
    if (touchIndex.current === null) return;

    const touch = e.touches[0];
    // Find which list item the finger is currently over
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!el) return;

    // Walk up the DOM to find a list item with a data-index attribute
    let target = el;
    while (target && target.dataset.index === undefined) {
      target = target.parentElement;
    }
    if (!target || target.dataset.index === undefined) return;

    const overIndex = parseInt(target.dataset.index, 10);
    if (isNaN(overIndex) || overIndex === touchIndex.current) return;

    const updated = [...visitOrder];
    const [moved] = updated.splice(touchIndex.current, 1);
    updated.splice(overIndex, 0, moved);
    touchIndex.current = overIndex;
    onReorder(updated);
  };

  const handleTouchEnd = () => { touchIndex.current = null; };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Back to browse — exits trip planning mode */}
      <Box component="button" onClick={onExitTripPlanning}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 2.5, py: 1.5, flexShrink: 0,
          background: 'none', border: 'none', cursor: 'pointer',
          borderBottom: `1px solid ${BORDER}`,
          color: MUTED,
          '&:hover': { color: GREEN },
          transition: 'color 0.15s ease',
        }}
      >
        <ArrowBackIcon sx={{ fontSize: '0.85rem' }} />
        <Typography sx={{ ...labelMono, fontSize: '9px' }}>BACK TO BROWSE</Typography>
      </Box>

      <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
        <Typography sx={{ ...labelMono, color: GREEN, fontSize: '9px' }}>MY VISIT PLAN</Typography>
        {/* Only relevant once there's something to drag */}
        {visitOrder.length > 0 && (
          <Typography sx={{ ...labelMono, color: MUTED_FAINT, fontSize: '8px', mt: 0.5 }}>
            Drag to reorder stops
          </Typography>
        )}
      </Box>

      {/* Draggable visit list — touch-friendly */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {visitOrder.length === 0 ? (
          <Box sx={{ px: 2.5, py: 3 }}>
            <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT }}>
              No shops bookmarked yet. Bookmark shops as you browse to add them here.
            </Typography>
          </Box>
        ) : (
          visitOrder.map((v, i) => (
            <Box
              key={v.googlePlaceId}
              ref={el => { itemRefs.current[i] = el; }}
              data-index={i}           // used by touch hit-test to identify the item
              draggable                // mouse drag (desktop)
              onDragStart={() => handleDragStart(i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDragEnd={handleDragEnd}
              onTouchStart={(e) => handleTouchStart(e, i)}  // touch drag (mobile)
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1.5,
                borderBottom: `1px solid ${BORDER}`,
                cursor: 'grab',
                backgroundColor: '#fff',
                userSelect: 'none',    // prevent text selection during drag
                WebkitUserSelect: 'none',
                touchAction: 'none',   // tell the browser we're handling touch ourselves
                '&:hover': { backgroundColor: STONE50 },
                transition: 'background-color 0.15s ease',
              }}
            >
              {/* Stop number */}
              <Box sx={{
                width: 20, height: 20, borderRadius: '50%',
                backgroundColor: '#ef4444', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Typography sx={{ ...labelMono, fontSize: '8px' }}>{i + 1}</Typography>
              </Box>

              {/* Vendor info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontSize: '0.78rem', fontWeight: 600, color: CHARCOAL, ...bodyFont,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {v.name}
                </Typography>
                {v.address && (
                  <Typography sx={{
                    fontSize: '0.7rem', color: MUTED_FAINT, mt: 0.25, ...bodyFont,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {v.address}
                  </Typography>
                )}
              </Box>

              {/* Drag handle */}
              <DragIndicatorIcon sx={{ fontSize: '1rem', color: MUTED_FAINT, flexShrink: 0 }} />
            </Box>
          ))
        )}
      </Box>

      {/* Google Maps CTA */}
      {visitOrder.length > 0 && googleMapsUrl && (
        <Box component="a"
          href={googleMapsUrl}
          target="_blank" rel="noopener noreferrer"
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            backgroundColor: GREEN, color: '#fff', textDecoration: 'none',
            px: 2, py: 1.5, ...labelMono, fontSize: '10px', flexShrink: 0,
            '&:hover': { backgroundColor: GREEN_HOVER },
            transition: 'background-color 0.2s ease',
          }}
        >
          <LocationOnIcon sx={{ fontSize: '0.875rem' }} />
          Open Route in Google Maps
        </Box>
      )}
    </Box>
  );
}

// ─── MobileBottomSheet ────────────────────────────────────────────────────────
// Two-tab structure: Filters & Categories / Plan My Route.
// The handle bar shows a bookmarking tip (no bookmarks yet) or a shortcut
// badge into the route tab (bookmarks exist) — but only while the sheet is
// collapsed. Once open, the tab bar below already covers that job.
function MobileBottomSheet({
  categories, activeCategory, onSelectCategory, vendorCount,
  ratingRange, setRatingRange, ratingTotalRange, setRatingTotalRange,
  bookmarkCount, onEnterTripPlanning, onExitTripPlanning,
  visitOrder, onReorder, googleMapsUrl,
}) {
  const [sheetState, setSheetState] = useState('peek'); // 'peek' | 'half'
  // activeTab only matters when sheet is open
  const [activeTab, setActiveTab]   = useState('filters'); // 'filters' | 'plan'

  const isOpen = sheetState === 'half';

  const toggleSheet = () => setSheetState(s => s === 'peek' ? 'half' : 'peek');

  // When the Plan My Route badge is tapped from peek state:
  // open the sheet and switch to the plan tab
  const handleOpenPlanTab = () => {
    setActiveTab('plan');
    setSheetState('half');
    onEnterTripPlanning();
  };

  return (
    <Box sx={{
      position: 'absolute', bottom: 0, left: 0, right: 0,
      zIndex: 999,
      height: isOpen ? ['55vh', '55dvh'] : '64px',
      transition: 'height 0.4s cubic-bezier(0.22,1,0.36,1)',
      backgroundColor: '#fff',
      borderTop: `1px solid ${BORDER}`,
      boxShadow: '0 -4px 24px rgba(0,0,0,0.10)',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>

      {/* ── Handle bar ── */}
      {/* Left: drag affordance to toggle the sheet.
          Right: shortcut badge into the route tab, shown only while the sheet
          is collapsed (peek) and bookmarks exist. Hidden once the sheet is
          open, since the tab bar below already covers that job. */}
      <Box sx={{
        flexShrink: 0, height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 3,
        borderBottom: isOpen ? `1px solid ${BORDER}` : 'none',
      }}>
        {/* Left side: tap to toggle sheet */}
        <Box onClick={toggleSheet} sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          cursor: 'pointer', userSelect: 'none', flex: 1,
        }}>
          <TuneIcon sx={{ fontSize: '0.9rem', color: MUTED }} />
          <Typography sx={{ ...labelMono, fontSize: '9px', color: CHARCOAL }}>
            {isOpen ? 'Collapse' : 'Filters & Categories'}
          </Typography>
          {isOpen && (
            <KeyboardArrowDownIcon sx={{ fontSize: '1rem', color: MUTED }} />
          )}
        </Box>

        {/* Right side: hidden once the sheet is open, or while there are no bookmarks */}
        {!isOpen && bookmarkCount > 0 && (
          <Box
            onClick={handleOpenPlanTab}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              cursor: 'pointer', userSelect: 'none',
              px: 1.5, py: 0.75,
              border: `1px solid ${BORDER}`,
              transition: 'all 0.15s ease',
            }}
          >
            <BookmarkIcon sx={{ fontSize: '0.75rem', color: GREEN }} />
            <Typography sx={{ ...labelMono, fontSize: '8px', color: GREEN }}>
              Plan My Route ({bookmarkCount})
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Tab bar (only shown when open) ── */}
      {isOpen && (
        <Box sx={{
          flexShrink: 0,
          display: 'flex',
          borderBottom: `1px solid ${BORDER}`,
        }}>
          <Box component="button"
            onClick={() => { setActiveTab('filters'); onExitTripPlanning(); }}
            sx={{
              flex: 1, background: 'none', border: 'none', cursor: 'pointer',
              py: 1.25, px: 2,
              borderBottom: `2px solid ${activeTab === 'filters' ? GREEN : 'transparent'}`,
              transition: 'border-color 0.15s ease',
            }}
          >
            <Typography sx={{ ...labelMono, fontSize: '9px', color: activeTab === 'filters' ? GREEN : MUTED }}>
              Filters & Categories
            </Typography>
          </Box>
          <Box component="button"
            onClick={() => { setActiveTab('plan'); onEnterTripPlanning(); }}
            sx={{
              flex: 1, background: 'none', border: 'none', cursor: 'pointer',
              py: 1.25, px: 2,
              borderBottom: `2px solid ${activeTab === 'plan' ? GREEN : 'transparent'}`,
              transition: 'border-color 0.15s ease',
            }}
          >
            <Typography sx={{ ...labelMono, fontSize: '9px', color: activeTab === 'plan' ? GREEN : MUTED }}>
              Plan My Route {bookmarkCount > 0 ? `(${bookmarkCount})` : ''}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Tab content ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>

        {/* ── Filters & Categories tab ── */}
        {activeTab === 'filters' && (
          <>
            {/* Vendor count — lives here, not in the handle bar */}
            <Box sx={{ px: 3, pt: 1.5, pb: 0.5 }}>
              <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT }}>
                {vendorCount} showroom{vendorCount !== 1 ? 's' : ''} shown
              </Typography>
            </Box>

            <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
              {/* Category rows — tapping an already-selected category deselects it,
                  returning to the default (unmissable) set */}
              {categories.map(cat => {
                const { Icon } = cat;
                const isActive = activeCategory === cat.key;
                return (
                  <Box key={cat.key} component="button"
                    onClick={() => onSelectCategory(isActive ? null : cat.key)}
                    sx={{
                      width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 3, py: 1.5, borderBottom: `1px solid ${BORDER}`,
                      backgroundColor: isActive ? 'rgba(33,131,51,0.05)' : 'transparent',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Icon sx={{ fontSize: '1rem', color: isActive ? GREEN : 'rgba(0,0,0,0.30)' }} />
                      <Typography sx={{ ...labelMono, fontSize: '9px', textAlign: 'left', color: isActive ? GREEN : MUTED }}>
                        {cat.label}
                      </Typography>
                    </Box>
                    {isActive && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: GREEN, flexShrink: 0 }} />}
                  </Box>
                );
              })}
            </Box>

            {/* Rating sliders */}
            <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
              <RatingFilterBlock
                ratingRange={ratingRange} setRatingRange={setRatingRange}
                ratingTotalRange={ratingTotalRange} setRatingTotalRange={setRatingTotalRange}
              />
            </Box>
          </>
        )}

        {/* ── Plan My Route tab ── */}
        {activeTab === 'plan' && (
          <VisitPlanPanel
            visitOrder={visitOrder}
            onReorder={onReorder}
            onExitTripPlanning={() => {
              onExitTripPlanning();
              setActiveTab('filters');
            }}
            googleMapsUrl={googleMapsUrl}
          />
        )}
      </Box>
    </Box>
  );
}

// ─── MobileImageCarousel ──────────────────────────────────────────────────────
function MobileImageCarousel({ images }) {
  const [imgIndex, setImgIndex] = useState(0);
  const [showHint, setShowHint] = useState(images.length > 1);

  useEffect(() => {
    if (!showHint) return;
    const t = setTimeout(() => setShowHint(false), 1200);
    return () => clearTimeout(t);
  }, []);

  const prev = () => setImgIndex(i => (i - 1 + images.length) % images.length);
  const next = () => setImgIndex(i => (i + 1) % images.length);

  return (
    <Box sx={{ position: 'relative', height: 180, overflow: 'hidden', mx: -3 }}>
      <Box component="img" src={images[imgIndex]} alt=""
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      {images.length > 1 && (
        <>
          <Box onClick={prev} sx={{ position: 'absolute', top: 0, left: 0, width: '33%', height: '100%', cursor: 'pointer' }} />
          <Box onClick={next} sx={{ position: 'absolute', top: 0, right: 0, width: '33%', height: '100%', cursor: 'pointer' }} />
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0,0,0,0.52)', px: '12px', py: '6px',
            opacity: showHint ? 1 : 0, transition: 'opacity 0.6s ease', pointerEvents: 'none',
          }}>
            <Typography sx={{ ...labelMono, color: '#fff', fontSize: '9px' }}>TAP TO BROWSE</Typography>
          </Box>
          <Box aria-hidden sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 48,
            background: 'linear-gradient(to top, rgba(0,0,0,0.45), transparent)',
            pointerEvents: 'none',
          }} />
          <Box sx={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1 }}>
            {images.map((_, i) => (
              <Box key={i} sx={{
                width: i === imgIndex ? 18 : 7, height: 7, borderRadius: '4px',
                backgroundColor: i === imgIndex ? '#fff' : 'rgba(255,255,255,0.70)',
                transition: 'width 0.25s ease, background-color 0.2s ease',
              }} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

// ─── ShopDetailPanel ──────────────────────────────────────────────────────────
// Desktop: slides in from the right when a vendor is selected.
function ShopDetailPanel({ vendor, bookmarked, onToggleBookmark, onClose }) {
  const images = useMemo(
    () => [vendor.image1, vendor.image2, vendor.image3, vendor.image4, vendor.image5].filter(Boolean),
    [vendor]
  );
  const [imgIndex,   setImgIndex]   = useState(0);
  const [ctaHovered, setCtaHovered] = useState(false);
  const [mounted,    setMounted]    = useState(false);

  useEffect(() => { setImgIndex(0); }, [vendor.googlePlaceId]);
  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => { cancelAnimationFrame(t); setMounted(false); };
  }, []);

  return (
    <Box sx={{
      position: 'absolute', top: 16, right: 16, zIndex: 1000,
      width: 340, backgroundColor: '#fff',
      border: `1px solid ${BORDER}`, boxShadow: '0 8px 40px rgba(0,0,0,0.14)',
      display: 'flex', flexDirection: 'column',
      maxHeight: 'calc(100% - 32px)', overflow: 'hidden',
      transform: mounted ? 'translateX(0)' : 'translateX(24px)',
      opacity:   mounted ? 1 : 0,
      transition: 'transform 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.25s ease',
    }}>
      {/* Image carousel */}
      {images.length > 0 && (
        <Box sx={{ position: 'relative', height: 180, flexShrink: 0, overflow: 'hidden',
          '&:hover .navArrow': { opacity: 1 } }}>
          <Box component="img" src={images[imgIndex]} alt={vendor.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={onToggleBookmark}
              sx={{ backgroundColor: 'rgba(255,255,255,0.95)', '&:hover': { backgroundColor: '#fff' } }}>
              {bookmarked
                ? <BookmarkIcon sx={{ color: '#FF385C' }} fontSize="small" />
                : <BookmarkBorderIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={onClose}
              sx={{ backgroundColor: 'rgba(255,255,255,0.95)', '&:hover': { backgroundColor: '#fff' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {images.length > 1 && (
            <>
              <IconButton className="navArrow" size="small"
                onClick={() => setImgIndex(i => (i - 1 + images.length) % images.length)}
                sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255,255,255,0.95)', opacity: 0, transition: 'opacity 0.2s',
                  '&:hover': { backgroundColor: '#fff' } }}>
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <IconButton className="navArrow" size="small"
                onClick={() => setImgIndex(i => (i + 1) % images.length)}
                sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255,255,255,0.95)', opacity: 0, transition: 'opacity 0.2s',
                  '&:hover': { backgroundColor: '#fff' } }}>
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
              <Box sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
                display: 'flex', gap: 0.6 }}>
                {images.map((_, i) => (
                  <Box key={i} sx={{ width: 5, height: 5, borderRadius: '50%',
                    backgroundColor: i === imgIndex ? '#fff' : 'rgba(255,255,255,0.45)',
                    transition: 'background-color 0.2s ease' }} />
                ))}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Scrollable content */}
      <Box sx={{ overflowY: 'auto', flex: 1, px: 2.5, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography sx={{ ...labelMono, color: GREEN, fontSize: '9px', mb: 0.5 }}>SHOP DETAILS</Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: CHARCOAL, ...bodyFont, lineHeight: 1.25 }}>
              {vendor.name}
            </Typography>
            {vendor.googleRating != null && (
              <Typography sx={{ fontSize: '0.8rem', color: '#f59e0b', mt: 0.5 }}>
                ★ {vendor.googleRating.toFixed(1)}{' '}
                <Typography component="span" sx={{ color: MUTED, fontSize: '0.75rem' }}>
                  ({vendor.googleRatingTotal?.toLocaleString()} reviews)
                </Typography>
              </Typography>
            )}
          </Box>
          {images.length === 0 && (
            <IconButton size="small" onClick={onClose}
              sx={{ border: `1px solid ${BORDER}`, borderRadius: 0, p: '4px', flexShrink: 0 }}>
              <CloseIcon sx={{ fontSize: '0.875rem', color: MUTED }} />
            </IconButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ ...labelMono, color: MUTED, fontSize: '9px' }}>PRACTICAL INFO</Typography>
          {vendor.address && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <LocationOnIcon sx={{ fontSize: '0.875rem', color: MUTED, flexShrink: 0, mt: '1px' }} />
              <Box>
                <Typography sx={{ fontSize: '0.8rem', color: CHARCOAL, ...bodyFont }}>{vendor.address}</Typography>
                <Box component="a"
                  href={`https://www.google.com/maps/search/${encodeURIComponent(vendor.name + ' ' + vendor.address)}`}
                  target="_blank" rel="noopener noreferrer"
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5,
                    ...labelMono, fontSize: '8px', color: GREEN, textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}>
                  Open in Google Maps <ChevronRightIcon sx={{ fontSize: '0.65rem' }} />
                </Box>
              </Box>
            </Box>
          )}
          {vendor.phoneNumber && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PhoneIcon sx={{ fontSize: '0.875rem', color: MUTED, flexShrink: 0 }} />
              <Box component="a" href={`tel:${vendor.phoneNumber}`}
                sx={{ fontSize: '0.8rem', color: CHARCOAL, textDecoration: 'none', ...bodyFont, '&:hover': { color: GREEN } }}>
                {vendor.phoneNumber}
              </Box>
            </Box>
          )}
          {vendor.website && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LanguageIcon sx={{ fontSize: '0.875rem', color: MUTED, flexShrink: 0 }} />
              <Box component="a"
                href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`}
                target="_blank" rel="noopener noreferrer"
                sx={{
                  fontSize: '0.8rem', color: GREEN, textDecoration: 'none', ...bodyFont,
                  '&:hover': { textDecoration: 'underline' },
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                {vendor.website}
              </Box>
            </Box>
          )}
          {vendor.instaAcct && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <InstagramIcon sx={{ fontSize: '0.875rem', color: MUTED, flexShrink: 0 }} />
              <Box component="a"
                href={`https://instagram.com/${vendor.instaAcct.replace('@', '')}`}
                target="_blank" rel="noopener noreferrer"
                sx={{ fontSize: '0.8rem', color: GREEN, textDecoration: 'none', ...bodyFont, '&:hover': { textDecoration: 'underline' } }}>
                {vendor.instaAcct}
              </Box>
            </Box>
          )}
        </Box>

        <Box component="a"
          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((vendor.address || '') + ', Singapore')}`}
          target="_blank" rel="noopener noreferrer"
          onMouseEnter={() => setCtaHovered(true)} onMouseLeave={() => setCtaHovered(false)}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            backgroundColor: ctaHovered ? GREEN_HOVER : GREEN,
            color: '#fff', textDecoration: 'none',
            px: 2, py: 1.5, ...labelMono, fontSize: '10px',
            transition: 'background-color 0.2s ease',
          }}
        >
          <LocationOnIcon sx={{ fontSize: '0.875rem' }} />
          Get Directions
        </Box>
      </Box>
    </Box>
  );
}

// ─── MobileDetailSheet ────────────────────────────────────────────────────────
// Slides up from the bottom when a marker is tapped on mobile.
function MobileDetailSheet({ vendor, bookmarked, onToggleBookmark, onClose, open }) {
  const images = useMemo(
    () => [vendor.image1, vendor.image2, vendor.image3, vendor.image4, vendor.image5].filter(Boolean),
    [vendor]
  );

  return (
    <Box sx={{
      position: 'absolute', bottom: 0, left: 0, right: 0,
      zIndex: 1001, // above MobileBottomSheet (999)
      backgroundColor: '#fff', borderTop: `1px solid ${BORDER}`,
      boxShadow: '0 -8px 32px rgba(0,0,0,0.12)',
      maxHeight: open ? '70vh' : '0px', overflow: 'hidden',
      transition: 'max-height 0.4s cubic-bezier(0.22,1,0.36,1)',
    }}>
      {/* Sticky header */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        px: 3, pt: 2.5, pb: 1.5, borderBottom: `1px solid ${BORDER}`,
        position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1,
      }}>
        <Box>
          <Typography sx={{ ...labelMono, color: GREEN, fontSize: '9px', mb: 0.25 }}>SHOP DETAILS</Typography>
          <Typography sx={{ fontSize: '0.925rem', fontWeight: 600, color: CHARCOAL, ...bodyFont }}>{vendor.name}</Typography>
          {vendor.googleRating != null && (
            <Typography sx={{ fontSize: '0.8rem', color: '#f59e0b', mt: 0.25 }}>
              ★ {vendor.googleRating.toFixed(1)}{' '}
              <Typography component="span" sx={{ color: MUTED, fontSize: '0.75rem' }}>
                ({vendor.googleRatingTotal?.toLocaleString()})
              </Typography>
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          <IconButton size="small" onClick={onToggleBookmark}
            sx={{ border: `1px solid ${BORDER}`, borderRadius: 0, p: '6px' }}>
            {bookmarked
              ? <BookmarkIcon sx={{ color: '#FF385C', fontSize: '1rem' }} />
              : <BookmarkBorderIcon sx={{ fontSize: '1rem' }} />}
          </IconButton>
          <IconButton size="small" onClick={onClose}
            sx={{ border: `1px solid ${BORDER}`, borderRadius: 0, p: '6px' }}>
            <CloseIcon sx={{ fontSize: '1rem', color: MUTED }} />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ overflowY: 'auto', maxHeight: 'calc(70vh - 90px)', px: 3, py: 2,
        display: 'flex', flexDirection: 'column', gap: 2 }}>
        {images.length > 0 && <MobileImageCarousel images={images} />}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {vendor.address && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <LocationOnIcon sx={{ fontSize: '0.875rem', color: MUTED, flexShrink: 0, mt: '1px' }} />
              <Typography sx={{ fontSize: '0.8rem', color: CHARCOAL, ...bodyFont }}>{vendor.address}</Typography>
            </Box>
          )}
          {vendor.phoneNumber && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PhoneIcon sx={{ fontSize: '0.875rem', color: MUTED, flexShrink: 0 }} />
              <Box component="a" href={`tel:${vendor.phoneNumber}`}
                sx={{ fontSize: '0.8rem', color: GREEN, textDecoration: 'none', ...bodyFont }}>
                {vendor.phoneNumber}
              </Box>
            </Box>
          )}
          {vendor.website && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LanguageIcon sx={{ fontSize: '0.875rem', color: MUTED, flexShrink: 0 }} />
              <Box component="a"
                href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`}
                target="_blank" rel="noopener noreferrer"
                sx={{ fontSize: '0.8rem', color: GREEN, textDecoration: 'none', ...bodyFont,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {vendor.website}
              </Box>
            </Box>
          )}
        </Box>
        <Box component="a"
          href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((vendor.address || '') + ', Singapore')}`}
          target="_blank" rel="noopener noreferrer"
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            backgroundColor: GREEN, color: '#fff', textDecoration: 'none',
            px: 2, py: 1.75, ...labelMono, fontSize: '10px',
            '&:active': { backgroundColor: GREEN_HOVER },
          }}
        >
          <LocationOnIcon sx={{ fontSize: '0.875rem' }} />
          Get Directions
        </Box>
      </Box>
    </Box>
  );
}

// ─── MapPage5 ─────────────────────────────────────────────────────────────────
export default function MapPage5() {
  const [vendors,           setVendors]           = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [loadError,         setLoadError]         = useState(false);
  const [activeCategory,    setActiveCategory]    = useState(null);
  const [expandedCategory,  setExpandedCategory]  = useState(null);
  const [ratingRange,       setRatingRange]       = useState([0, 5]);
  const [ratingTotalRange,  setRatingTotalRange]  = useState([0, 5000]);
  const [selectedVendorId,  setSelectedVendorId]  = useState(null);
  const [mobileSheetOpen,   setMobileSheetOpen]   = useState(false);
  const [mapZoom,           setMapZoom]           = useState(11);
  const [tripPlanningMode,  setTripPlanningMode]  = useState(false);
  const [visitOrder,        setVisitOrder]        = useState([]); // ordered list of vendor objects

  // ── Bookmarks: persisted to localStorage as full vendor objects ──────────────
  // Initialise from localStorage on first render so bookmarks survive page refresh.
  const [bookmarkedVendors, setBookmarkedVendors] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); }
    catch { return []; }
  });

  // Keep localStorage in sync whenever bookmarks change
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(bookmarkedVendors));
  }, [bookmarkedVendors]);

  const fetchTimeoutRef = useRef(null);
  const markerRefs      = useRef({});

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchVendors = useCallback(async () => {
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const params = new URLSearchParams({ ...(activeCategory && { category: activeCategory }) });
        const data = await apiFetch(`/api/vendor-map?${params}`, { auth: false });
        setVendors(data || []);
      } catch (err) {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [activeCategory]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => () => { if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current); }, []);

  // ── Client-side rating filter ──────────────────────────────────────────────
  const filteredVendors = useMemo(() => vendors.filter(v => {
    const ratingOk = (v.googleRating      || 0) >= ratingRange[0] && (v.googleRating || 0) <= ratingRange[1];
    const totalOk  = (v.googleRatingTotal || 0) >= ratingTotalRange[0] &&
      (ratingTotalRange[1] === 5000 ? true : (v.googleRatingTotal || 0) <= ratingTotalRange[1]);
    return ratingOk && totalOk;
  }), [vendors, ratingRange, ratingTotalRange]);

  // ── selectedVendor lookup: check filteredVendors AND bookmarked vendors ────
  // This is important: a bookmarked vendor may not be in filteredVendors if
  // the user has switched category or adjusted the rating filter.
  const selectedVendor = useMemo(() => {
    if (selectedVendorId == null) return null;
    return (
      filteredVendors.find(v => v.googlePlaceId === selectedVendorId) ??
      bookmarkedVendors.find(v => v.googlePlaceId === selectedVendorId) ??
      null
    );
  }, [selectedVendorId, filteredVendors, bookmarkedVendors]);

  // ── Bookmark handlers ──────────────────────────────────────────────────────
  const isBookmarked = useCallback(
    (googlePlaceId) => bookmarkedVendors.some(v => v.googlePlaceId === googlePlaceId),
    [bookmarkedVendors]
  );

  const handleToggleBookmark = useCallback((vendor) => {
    // vendor is the full object — we need it to store if adding
    setBookmarkedVendors(prev => {
      const exists = prev.some(v => v.googlePlaceId === vendor.googlePlaceId);
      const updated = exists
        ? prev.filter(v => v.googlePlaceId !== vendor.googlePlaceId)
        : [...prev, vendor];
      // If we just removed the last bookmark, exit trip planning mode
      if (updated.length === 0) setTripPlanningMode(false);
      return updated;
    });
  }, []);

  // ── Trip planning mode ─────────────────────────────────────────────────────
  // On entry: sort bookmarks by nearest-neighbour and set as visit order.
  const handleEnterTripPlanning = useCallback(() => {
    setTripPlanningMode(true);
    setVisitOrder(sortByNearestNeighbour(bookmarkedVendors));
    setSelectedVendorId(null); // close any open detail panel
  }, [bookmarkedVendors]);

  const handleExitTripPlanning = useCallback(() => {
    setTripPlanningMode(false);
  }, []);

  // Keep visitOrder in sync if bookmarks change while in trip planning mode
  // (e.g. user unbookmarks a shop from the detail panel)
  useEffect(() => {
    if (!tripPlanningMode) return;
    setVisitOrder(prev => {
      // Remove any vendors that are no longer bookmarked
      const bookmarkedIds = new Set(bookmarkedVendors.map(v => v.googlePlaceId));
      return prev.filter(v => bookmarkedIds.has(v.googlePlaceId));
    });
  }, [bookmarkedVendors, tripPlanningMode]);

  // ── Google Maps URL for the current visit order ────────────────────────────
  const googleMapsUrl = useMemo(
    () => buildGoogleMapsUrl(visitOrder),
    [visitOrder]
  );

  // ── Other handlers ─────────────────────────────────────────────────────────
  const handleCategoryClick = (key) => {
    setActiveCategory(prev  => prev === key ? null : key);
    setExpandedCategory(prev => prev === key ? null : key);
    setSelectedVendorId(null);
  };

  const handleMarkerClick = (v) => {
    setSelectedVendorId(v.googlePlaceId);
    setMobileSheetOpen(true);
  };

  const handleSelectVendor = (id) => { setSelectedVendorId(id); };

  const getMarkerIcon = useCallback((v) => {
    if (isBookmarked(v.googlePlaceId)) return redIcon;
    const score = activeCategory ? v[activeCategory] : null;
    if (typeof score === 'number' && score >= 1 && score <= 5) return yellowIcon;
    return greenIcon;
  }, [isBookmarked, activeCategory]);

  useEffect(() => { markerRefs.current = {}; }, [filteredVendors]);

  const showTooltips = mapZoom >= TOOLTIP_MIN_ZOOM;

  // Props shared between desktop and mobile map instances
  const sharedMapProps = {
    filteredVendors,
    bookmarkedVendors,
    tripPlanningMode,
    getMarkerIcon,
    markerRefs,
    onMarkerClick: handleMarkerClick,
    showTooltips,
  };

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', backgroundColor: '#fff', ...bodyFont,
      // On mobile: constrain to exactly the viewport so there's no empty strip below the map.
      // On desktop: allow the page to grow beyond the viewport (page header + footer need room).
      height: { xs: ['100vh', '100dvh'], md: 'auto' },
      minHeight: { xs: 'unset', md: '100vh' },
      overflow: { xs: 'hidden', md: 'visible' },
    }}>

      {/* Force solid navbar background on mobile — the navbar normally goes transparent
          at scrollY=0 but the map page never scrolls. This overrides that for mobile only. */}
      <style>{`@media (max-width:899px){nav{background-color:rgba(255,255,255,0.97)!important;border-bottom:1px solid #e5e7eb!important;}}`}</style>
      <Navbar />

      {/* Page header — hidden on mobile (map fills screen below navbar) */}
      <Box sx={{ pt: '64px', display: { xs: 'none', md: 'block' } }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 3, md: 6 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: { xs: 5, md: 4 }, mb: 2 }}>
            <Typography sx={{ ...labelMono, color: GREEN }}>Resources</Typography>
            <Box sx={{ height: '1px', width: 40, backgroundColor: 'rgba(33,131,51,0.30)' }} />
          </Box>
          <Typography component="h1" sx={{
            fontSize: 'clamp(2rem, 5vw, 3.8rem)',
            fontWeight: 300, lineHeight: 1.06, letterSpacing: '-0.03em',
            color: CHARCOAL, mb: 2, ...bodyFont,
          }}>
            Showrooms{' '}
            <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 300, color: GREEN }}>worth visiting</Box>
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: MUTED, lineHeight: 1.7, maxWidth: 580, mb: 4, ...bodyFont }}>
            What looks great in a photo can feel different in person — colour, texture, finish.
            Browse the showrooms below, filter by category or rating, and click any pin for details and directions.
          </Typography>
        </Box>
      </Box>

      {/* On mobile: map fills exactly the space below the fixed 64px navbar */}
      <Box sx={{
        position: 'relative',
        height: { xs: ['calc(100vh - 64px)', 'calc(100dvh - 64px)'], md: 'auto' },
        flex: { xs: 'none', md: 1 },
        mt: { xs: '64px', md: 0 }, // push down below fixed navbar on mobile
      }}>

        {/* Couldn't load the showrooms: say so above the map, with a retry */}
        {loadError && (
          <Box sx={{
            position: { xs: 'absolute', md: 'static' }, top: 12, left: 12, right: 12, zIndex: 1100,
            maxWidth: 1400, mx: 'auto', px: { xs: 0, md: 6 }, pb: { md: 2 },
          }}>
            <InlineAlert severity="error">
              We couldn't load the showrooms.{' '}
              <Box component="button" type="button" onClick={fetchVendors}
                sx={{ background: 'none', border: 'none', p: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}>
                Try again
              </Box>
            </InlineAlert>
          </Box>
        )}

        {/* ════════ DESKTOP ════════ */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, maxWidth: 1400, mx: 'auto', px: 6, pb: 4, gap: 3 }}>

          {/* ── Left panel ─────────────────────────────────────────────────────
               Two named tabs always visible at the top:
                 Browse         — rating filter + category list
                 Plan My Route  — visit order list (or empty state invitation)

               tripPlanningMode drives which tab is active. Switching tabs
               also enters/exits trip planning mode so the map responds.
          ─────────────────────────────────────────────────────────────────────── */}
          <Box sx={{
            width: 268, flexShrink: 0, border: `1px solid ${BORDER}`,
            display: 'flex', flexDirection: 'column', overflow: 'hidden', height: 680,
          }}>

            {/* ── Tab bar — always present ──────────────────────────────────── */}
            <Box sx={{ display: 'flex', flexShrink: 0, borderBottom: `1px solid ${BORDER}` }}>

              {/* Browse tab */}
              <Box component="button"
                onClick={handleExitTripPlanning}
                sx={{
                  flex: 1, background: 'none', border: 'none', cursor: 'pointer',
                  py: 1.25, px: 1,
                  borderBottom: `2px solid ${!tripPlanningMode ? GREEN : 'transparent'}`,
                  transition: 'border-color 0.15s ease',
                  '&:hover': { backgroundColor: STONE50 },
                }}
              >
                <Typography sx={{
                  ...labelMono, fontSize: '9px',
                  color: !tripPlanningMode ? GREEN : MUTED,
                  transition: 'color 0.15s ease',
                }}>
                  Browse
                </Typography>
              </Box>

              {/* Plan My Route tab — count shown inline when bookmarks exist */}
              <Box component="button"
                onClick={handleEnterTripPlanning}
                sx={{
                  flex: 1, background: 'none', border: 'none', cursor: 'pointer',
                  py: 1.25, px: 1,
                  borderBottom: `2px solid ${tripPlanningMode ? GREEN : 'transparent'}`,
                  transition: 'border-color 0.15s ease',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  '&:hover': { backgroundColor: STONE50 },
                }}
              >
                <Typography sx={{
                  ...labelMono, fontSize: '9px',
                  color: tripPlanningMode ? GREEN : MUTED,
                  transition: 'color 0.15s ease',
                }}>
                  Plan My Route {bookmarkedVendors.length > 0 ? `(${bookmarkedVendors.length})` : ''}
                </Typography>
              </Box>
            </Box>

            {/* ── Browse tab content ────────────────────────────────────────── */}
            {!tripPlanningMode && (
              <>
                {/* Rating filter */}
                <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
                  <RatingFilterBlock
                    ratingRange={ratingRange} setRatingRange={setRatingRange}
                    ratingTotalRange={ratingTotalRange} setRatingTotalRange={setRatingTotalRange}
                    shownCount={filteredVendors.length}
                  />
                </Box>

                {/* Category section header */}
                <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
                  <Typography sx={{ ...labelMono, color: GREEN, fontSize: '9px' }}>
                    WHAT ARE YOU SHOPPING FOR?
                  </Typography>
                  {/* Bookmarking tip — only shown before the user has bookmarked
                      anything; disappears once they have, since it's no longer needed. */}
                  {bookmarkedVendors.length === 0 && (
                    <Typography sx={{ ...labelMono, color: MUTED_FAINT, fontSize: '8px', lineHeight: 1.6, mt: 0.75 }}>
                      Tip: bookmark shops as you browse to add them to your visit route.
                    </Typography>
                  )}
                </Box>

                {/* Category list — scrollable */}
                <Box sx={{ overflowY: 'auto', flex: 1 }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress size={20} sx={{ color: GREEN }} />
                    </Box>
                  ) : (
                    CATEGORY_DEFS.map(cat => (
                      <CategoryItem key={cat.key} cat={cat}
                        isActive={activeCategory === cat.key}
                        isExpanded={expandedCategory === cat.key}
                        vendors={filteredVendors}
                        selectedVendorId={selectedVendorId}
                        onSelect={handleSelectVendor}
                        onToggle={() => handleCategoryClick(cat.key)}
                      />
                    ))
                  )}
                </Box>
              </>
            )}

            {/* ── Plan My Route tab content ──────────────────────────────────── */}
            {tripPlanningMode && (
              bookmarkedVendors.length === 0 ? (
                // Empty state — centred invitation, reads as a feature not a footnote
                <Box sx={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'flex-start',
                  px: 3, pt: 4, textAlign: 'center', gap: 1.5,
                }}>
                  <BookmarkBorderIcon sx={{ fontSize: '1.75rem', color: MUTED_FAINT }} />
                  <Typography sx={{ ...labelMono, color: MUTED, fontSize: '9px', lineHeight: 1.7 }}>
                    Bookmark shops as you browse — then come back here to plan the optimal route between them.
                  </Typography>
                  <Box component="button"
                    onClick={handleExitTripPlanning}
                    sx={{
                      mt: 0.5, background: 'none', border: `1px solid ${BORDER}`,
                      cursor: 'pointer', px: 2, py: 1,
                      '&:hover': { borderColor: GREEN },
                      transition: 'border-color 0.15s ease',
                    }}
                  >
                    <Typography sx={{ ...labelMono, fontSize: '8px', color: MUTED }}>
                      ← Back to Browse
                    </Typography>
                  </Box>
                </Box>
              ) : (
                // Visit plan list
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <VisitPlanPanel
                    visitOrder={visitOrder}
                    onReorder={setVisitOrder}
                    onExitTripPlanning={handleExitTripPlanning}
                    googleMapsUrl={googleMapsUrl}
                  />
                </Box>
              )
            )}
          </Box>

          {/* Desktop map */}
          <Box sx={{ flex: 1, position: 'relative', height: 680, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
            <MapContainer center={DEFAULT_CENTER} zoom={11} minZoom={TOOLTIP_MIN_ZOOM}
              style={{ width: '100%', height: '100%' }} scrollWheelZoom zoomControl={false}>
              <TileLayer attribution='&copy; <a href="https://carto.com/">CARTO</a>' url={CARTO_URL} />
              <ZoomWatcher onZoomChange={setMapZoom} />
              <FitBoundsToBookmarks
                vendors={bookmarkedVendors}
                active={tripPlanningMode}
                paddingTop={48} paddingRight={48} paddingBottom={48} paddingLeft={48}
              />
              {/* Fit to all vendors in the selected category when category changes.
                  Suppressed in trip planning mode — FitBoundsToBookmarks owns the
                  viewport there. */}
              {!tripPlanningMode && (
                <FitBoundsToCategory
                  filteredVendors={filteredVendors}
                  paddingTop={48} paddingRight={48} paddingBottom={48} paddingLeft={48}
                />
              )}
              <MapMarkers {...sharedMapProps} />
              {tripPlanningMode && <TripPolyline orderedVendors={visitOrder} />}
              {/* Single-step pan: pin lands in centre of visible area left of the 340px panel */}
              {selectedVendor && !tripPlanningMode && (
                <PanToPin vendor={selectedVendor} panelWidth={340} panelHeight={0} />
              )}
            </MapContainer>

            {/* Detail panel — not shown in trip planning mode to avoid clutter */}
            {selectedVendor && !tripPlanningMode && (
              <ShopDetailPanel
                key={selectedVendor.googlePlaceId}
                vendor={selectedVendor}
                bookmarked={isBookmarked(selectedVendor.googlePlaceId)}
                onToggleBookmark={() => handleToggleBookmark(selectedVendor)}
                onClose={() => setSelectedVendorId(null)}
              />
            )}
          </Box>
        </Box>

        {/* ════════ MOBILE ════════ */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, position: 'absolute', inset: 0 }}>
          {/* attributionControl={false} removes the Leaflet | CARTO badge on mobile */}
          <MapContainer center={DEFAULT_CENTER} zoom={11} minZoom={TOOLTIP_MIN_ZOOM}
            style={{ width: '100%', height: '100%' }} scrollWheelZoom zoomControl={false}
            attributionControl={false}>
            <TileLayer url={CARTO_URL} />
            <ZoomWatcher onZoomChange={setMapZoom} />
            {/* paddingBottom accounts for the 55vh bottom sheet covering the lower map.
                We use window.innerHeight * 0.55 + 32px breathing room.
                paddingTop accounts for the 64px fixed navbar. */}
            <FitBoundsToBookmarks
              vendors={bookmarkedVendors}
              active={tripPlanningMode}
              paddingTop={80}
              paddingRight={48}
              paddingBottom={typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.55) + 32 : 480}
              paddingLeft={48}
            />
            {/* Same asymmetric padding for category fit — sheet still covers bottom */}
            {!tripPlanningMode && (
              <FitBoundsToCategory
                filteredVendors={filteredVendors}
                paddingTop={80}
                paddingRight={48}
                paddingBottom={typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.55) + 32 : 480}
                paddingLeft={48}
              />
            )}
            <MapMarkers {...sharedMapProps} />
            {tripPlanningMode && <TripPolyline orderedVendors={visitOrder} />}
            {/* Single-step pan: pin lands in centre of visible strip above the bottom sheet.
                The sheet covers 55vh when open. We use window.innerHeight * 0.55 as the
                obscured height so the calculation matches the actual rendered sheet. */}
            {selectedVendor && !tripPlanningMode && (
              <PanToPin
                vendor={selectedVendor}
                panelWidth={0}
                panelHeight={typeof window !== 'undefined' ? window.innerHeight * 0.55 : 400}
              />
            )}
          </MapContainer>

          <MobileBottomSheet
            categories={CATEGORY_DEFS}
            activeCategory={activeCategory}
            onSelectCategory={handleCategoryClick}
            vendorCount={filteredVendors.length}
            ratingRange={ratingRange} setRatingRange={setRatingRange}
            ratingTotalRange={ratingTotalRange} setRatingTotalRange={setRatingTotalRange}
            bookmarkCount={bookmarkedVendors.length}
            onEnterTripPlanning={handleEnterTripPlanning}
            onExitTripPlanning={handleExitTripPlanning}
            visitOrder={visitOrder}
            onReorder={setVisitOrder}
            googleMapsUrl={googleMapsUrl}
          />

          {/* Vendor detail sheet — not shown in trip planning mode */}
          {selectedVendor && !tripPlanningMode && (
            <MobileDetailSheet
              vendor={selectedVendor}
              bookmarked={isBookmarked(selectedVendor.googlePlaceId)}
              onToggleBookmark={() => handleToggleBookmark(selectedVendor)}
              onClose={() => { setSelectedVendorId(null); setMobileSheetOpen(false); }}
              open={mobileSheetOpen}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Footer />
      </Box>
    </Box>
  );
}